import i18n from 'i18next';
import { all, call, delay, put, race, select, takeLatest, take } from 'redux-saga/effects';
import { getTypoPairs } from '../../pages/exercises/services/Misc';
import { historyPush } from '../../services/History';
import { getUserToken } from '../live/Helpers';
import { getExerciseType } from './services/Misc';
import { processRequest } from '../../services/Api';
import { TYPO_CHECKER_API_URL } from '../../services/Constants';
import { formatStringToBase64, nextExerciseDetailsActionTypes } from './Constants';
import { getCurrentAnsweredQuestionId, getExerciseDone, isLoading } from './Selectors';
import * as errorHandlerActions from '../../components/errorHandler/Actions';
import * as notificationActions from '../../components/notification/Actions';
import * as chapterDetailsActions from '../chapterDetails/Actions';
import * as nextExerciseDetailsActions from './Actions';
import * as liveActions from '../live/Actions';

export default function* () {
	yield all([
		yield takeLatest(
			nextExerciseDetailsActionTypes.GET_QUESTION_EXPLANATION,
			handleQuestionExplanationRequest,
		),
		yield takeLatest(
			nextExerciseDetailsActionTypes.CREATE_EXERCISE,
			handleCreateExerciseRequest,
		),
		yield takeLatest(
			nextExerciseDetailsActionTypes.RESUME_EXERCISE_REQUEST,
			handleResumePausedExerciseRequest,
		),
		yield takeLatest(
			nextExerciseDetailsActionTypes.SEND_QUESTION_ANSWER_REQUEST,
			handleSendAnswerRequest,
		),
		yield takeLatest(nextExerciseDetailsActionTypes.STOP_TEST, handleStopTestRequest),
		yield takeLatest(nextExerciseDetailsActionTypes.STORE_TIME, handleStoreTimeRequest),
		yield takeLatest(
			nextExerciseDetailsActionTypes.STORE_TIME_BETWEEN,
			handleStoreTimeBetweenRequest,
		),
		yield takeLatest(
			nextExerciseDetailsActionTypes.CHECK_WORDS_BY_TYPO,
			handleCheckWordsByTypo,
		),
		yield takeLatest(
			nextExerciseDetailsActionTypes.GET_ADDITIONAL_EXPLANATION_REQUEST,
			handleGetAdditionalExplanationRequest,
		),
		yield takeLatest(
			nextExerciseDetailsActionTypes.CHECK_ANSWER_REQUEST,
			handleCheckAnswerRequest,
		),
		yield takeLatest(
			nextExerciseDetailsActionTypes.PERSONALIZED_QUESTION_REQUEST,
			handlePersonalizedQuestionRequest,
		),
		yield takeLatest(
			nextExerciseDetailsActionTypes.GET_AI_EXPLANATION_REQUEST,
			handleGetAiExplanationRequest,
		),
		yield takeLatest(
			nextExerciseDetailsActionTypes.GET_TOPIC_EXPLANATION,
			handleGetTopicExplanationRequest,
		),
	]);
}

export function* handleQuestionExplanationRequest({ payload }) {
	try {
		const { questionId, source, exerciseId, userAnswers, isPreview } = payload || {};

		const formattedAnswers =
			userAnswers && !!userAnswers.length && formatStringToBase64(userAnswers.join(', '));

		const requestData = {
			source,
			exercise_id: exerciseId,
			question_id: questionId,
			...(formattedAnswers ? { user_answer: formattedAnswers } : {}),
		};

		if (isPreview) requestData.preview = true;

		const { data } = yield call(
			processRequest,
			`public/questions/${questionId}/explanation`,
			'GET',
			requestData,
		);

		yield put(nextExerciseDetailsActions.getQuestionExplanationSuccess(data.explanation));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(nextExerciseDetailsActions.getQuestionExplanationError());
	}
}

export function* handleCreateExerciseRequest({ payload }) {
	const { createData, restartId, callback } = payload || {};
	const { exercise } = createData || {};
	try {
		const { data } = yield call(
			processRequest,
			`stage_exercises${restartId ? `/${restartId}/restart` : ''}`,
			'POST',
			createData,
		);
		const { exercise: newExercise } = data || {};

		yield put(nextExerciseDetailsActions.createExerciseSuccess(newExercise));
		yield call(historyPush, `/exercise/${newExercise?.id}/${getExerciseType(exercise?.stage)}`);

		if (callback) callback();
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(nextExerciseDetailsActions.createExerciseError());
		if (callback) callback();
	}
}

export function* handleResumePausedExerciseRequest({ payload = {} }) {
	try {
		const { id: exerciseId, source } = payload;
		const query = source ? { source } : {};
		const { data } = yield call(processRequest, `stage_exercises/${exerciseId}`, 'GET', query);

		yield put(nextExerciseDetailsActions.createExerciseSuccess(data.exercise));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(nextExerciseDetailsActions.resumeExerciseError());
	}
}

export function* handleSendAnswerRequest({ payload }) {
	try {
		const { data, code } = payload || {};
		const {
			exercise_answer: { exercise_id, is_ai_open_answer },
		} = data;
		let userToken = null;

		if (code) {
			userToken = getUserToken(code);
		}

		const { data: answerSummary } = yield call(
			processRequest,
			`exercises/${exercise_id}/exercise_answers`,
			'POST',
			data,
			false,
			false,
			userToken ? { 'X-Live-Battle-Room-Token': userToken } : {},
		);
		const { exercise_answer } = answerSummary || {};
		const { live_battle_answer_summary, streak } = exercise_answer || {};

		if (code) yield put(liveActions.setLiveBattleAnswerSummary(live_battle_answer_summary));
		if (!is_ai_open_answer) yield put(nextExerciseDetailsActions.sendAnswerSuccess({ streak }));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(nextExerciseDetailsActions.sendAnswerError());
	}
}

export function* handleStopTestRequest({ payload = {} }) {
	try {
		const { exerciseId } = payload;

		yield call(processRequest, `exercises/${exerciseId}/terminated_exercises`, 'POST');

		yield put(chapterDetailsActions.getToDoTopics(exerciseId));
	} catch (e) {
		const { callback } = payload;
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(nextExerciseDetailsActions.stopTestRequestError());
		callback && callback();
	}
}

export function* handleStoreTimeRequest(action) {
	try {
		const { id, time } = action.payload;
		const exerciseDone = yield select(getExerciseDone);
		const isProcessing = yield select(isLoading);

		if (isProcessing) {
			yield take(nextExerciseDetailsActionTypes.SEND_QUESTION_ANSWER_SUCCESS);
		}

		yield call(processRequest, `stage_exercises/${id}`, 'PATCH', { time_spent: time });

		if (!exerciseDone) {
			yield put(
				notificationActions.createNotification(i18n.t('exercise.stop_timer_success')),
			);
		}
	} catch (e) {
		yield put(nextExerciseDetailsActions.stopTestRequestError());
	}
}

export function* handleStoreTimeBetweenRequest(action) {
	try {
		const answerId = yield select(getCurrentAnsweredQuestionId);

		const { id, time } = action.payload;
		yield call(processRequest, `exercises/${id}/exercise_answers/${answerId}`, 'PATCH', {
			time_spent: time,
		});
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
	}
}

export function* handleCheckWordsByTypo({ payload }) {
	const { wordTuples } = payload || {};

	try {
		const requestPayload = {
			string_tuples: wordTuples,
		};

		const { typoCheckerData, timeout } = yield race({
			typoCheckerData: call(
				processRequest,
				TYPO_CHECKER_API_URL,
				'POST',
				requestPayload,
				false,
				true,
			),
			timeout: delay(500),
		});
		if (timeout || typoCheckerData.data.length === 0)
			throw new Error('timeout of 500ms exceeded');

		yield put(nextExerciseDetailsActions.checkWordsByTypoSuccess(typoCheckerData.data));
	} catch (e) {
		const formattedData = getTypoPairs(wordTuples);

		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(nextExerciseDetailsActions.checkWordsByTypoSuccess(formattedData));
	}
}

export function* handleGetAdditionalExplanationRequest(action) {
	try {
		const { questionId, previousExplanations } = action.payload;

		const { data } = yield call(
			processRequest,
			`questions/${questionId}/explanation/additional`,
			'POST',
			{
				previous_explanations: previousExplanations,
			},
		);

		yield put(nextExerciseDetailsActions.getAdditionalExplanationSuccess(data.new_explanation));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
	}
}

export function* handleCheckAnswerRequest(action) {
	try {
		const { exerciseId, questionId, userAnswer, expectedAnswer } = action.payload || {};

		const { data } = yield call(
			processRequest,
			`exercises/${exerciseId}/user_answers`,
			'POST',
			{
				question_id: questionId,
				answer: userAnswer,
				expected_answer: expectedAnswer,
			},
		);

		yield put(nextExerciseDetailsActions.checkAnswerSuccess(data));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(nextExerciseDetailsActions.checkAnswerError());
	}
}

export function* handlePersonalizedQuestionRequest({ payload }) {
	try {
		const { exerciseId, questionId, interests, question, expectedAnswer } = payload || {};

		const { data } = yield call(
			processRequest,
			`exercises/${exerciseId}/personalized_question`,
			'POST',
			{
				interests,
				question,
				answer: expectedAnswer,
				question_id: questionId,
			},
		);

		yield put(nextExerciseDetailsActions.personalizedQuestionSuccess(data.new_question));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(nextExerciseDetailsActions.personalizedQuestionError());
	}
}

export function* handleGetAiExplanationRequest({ payload }) {
	try {
		const { exerciseId, questionId, userAnswer } = payload || {};

		const { data } = yield call(
			processRequest,
			`exercises/${exerciseId}/ai_explanation`,
			'POST',
			{
				user_answer: userAnswer,
				question_id: questionId,
			},
		);

		const { request_id } = data || {};

		yield put(nextExerciseDetailsActions.setRequestId(request_id));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(nextExerciseDetailsActions.getAiExplanationError());
	}
}

export function* handleGetTopicExplanationRequest({ payload }) {
	try {
		const { topicId, streamId } = payload || {};

		const { data } = yield call(
			processRequest,
			`public/topics/${topicId}/summary${streamId ? `?stream_id=${streamId}` : ''}`,
		);

		yield put(nextExerciseDetailsActions.getTopicExplanationSuccess(data.summary));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(nextExerciseDetailsActions.getTopicExplanationError());
	}
}
